import * as SelectPrimitive from '@radix-ui/react-select';
import { PlatformList, platform } from '@visiba/std';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { restrictedZIndexes } from '../../../utils';
import { useText } from '../../../utils/hooks/use_text';
import { useCellulaProviderContext } from '../../provider';
import { Text } from '../../text';
import { LabelProps } from '../base/label.component';
import { useInputSelectContext } from './_input_select.context';
import { theme } from './input_select.theme';

const elements = {
	sheet: styled(SelectPrimitive.Content)`
		z-index: ${restrictedZIndexes.DANGER};
		overflow: hidden;
	`,
	sheetBackground: styled(motion.div)`
		background: rgba(0, 0, 0, 0.3);
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: ${restrictedZIndexes.DANGER};
	`,
	sheetContent: styled(motion.div)`
		text-align: center;
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		max-height: 70vh;
		z-index: ${restrictedZIndexes.DANGER};

		/* A pseudo element is used as the content filling, it comes with the benefit to extend the
	   height without affecting the layout. */
		&::before {
			content: '';
			display: block;
			border-radius: ${theme.getValue('contentBorderRadius')} ${theme.getValue('contentBorderRadius')} 0 0;
			background-color: ${theme.getValue('contentBackground')};
			/* Needs to be x bigger so the sheet may go under the viewport. It's for the cases where
			 the user drags the sheet upwards, will look wonky without it. */
			height: calc(100vh * 2);

			z-index: -1;
			position: absolute;
			top: 0;
			left: 0;
			right: 0;

			box-shadow: 0 -5px 25px 7px rgb(0 0 0 / 20%);
		}
	`,
	sheetHeader: styled.div`
		display: flex;
		align-items: center;
		gap: ${coreTokens.spacing.x0_5};
		padding: 0 ${coreTokens.spacing.x2};
		border-bottom: 1px solid ${theme.getValue('contentBorderColor')};
		min-height: ${theme.getValue('itemHeight')};
	`,
	sheetScrollable: styled.div`
		max-height: calc(70vh - ${theme.getValue('itemHeight')});
		width: 100%;
		overflow-y: auto;
	`,
	styledContent: styled(SelectPrimitive.Content)`
		pointer-events: none;
		z-index: ${restrictedZIndexes.high};
		overflow: hidden;
		background-color: ${theme.getValue('contentBackground')};
		border-radius: ${theme.getValue('contentBorderRadius')};
		border: 1px solid ${theme.getValue('contentBorderColor')};
		box-shadow: ${theme.getValue('contentBoxShadow')};
	`,
	styledSelectPrimitivePortal: styled(SelectPrimitive.Portal)`
		z-index: ${restrictedZIndexes.DANGER};
	`,
};

interface Props extends SelectPrimitive.SelectContentProps {
	labelProps: LabelProps;
}

export function CellulaInputSelectContent({ labelProps, children, ...props }: Props): JSX.Element | null {
	const context = useInputSelectContext();
	const { portalElement } = useCellulaProviderContext();
	const isMobile = platform.platformIs(PlatformList.Mobile);
	const staticTexts = useText();

	return (
		<AnimatePresence>
			<elements.styledSelectPrimitivePortal container={portalElement}>
				{isMobile ? (
					<>
						{context.isOpen ? <elements.sheetBackground initial={{ opacity: 0 }} animate={{ opacity: 1 }} /> : null}
						<elements.sheet {...props}>
							<elements.sheetContent
								initial={{ y: '100%' }}
								animate={{ y: 0 }}
								transition={{
									type: 'spring',
									stiffness: 200,
									damping: 20,
								}}
							>
								<elements.sheetHeader>
									<Text variant='labelSmallSemiBold'>{labelProps.children}</Text>
									{labelProps.optional ? <Text variant='labelXSmallRegular'> ({staticTexts('Component.Label.Optional')})</Text> : null}
								</elements.sheetHeader>
								<elements.sheetScrollable>{children}</elements.sheetScrollable>
							</elements.sheetContent>
						</elements.sheet>
					</>
				) : (
					<elements.styledContent {...props}>{children}</elements.styledContent>
				)}
			</elements.styledSelectPrimitivePortal>
		</AnimatePresence>
	);
}
